import { useState } from 'react';
import { MapPin, Package, Plus, Trash2, Truck, CheckCircle2 } from 'lucide-react';

import type { AdminStore, StoreShippingOption } from '@/client/types/stores';
import { useUpdateStoreSettings } from '@/client/api/stores/stores.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { Input } from '@/components/ui/input';
import type { StoreOrder } from '@/client/types/stores/parity';
import { useStoreSettingsQuery } from '@/client/api/stores/stores.query';
import { useStoreOrderListQuery } from '@/client/api/stores/parity.query';
import { cn } from '@/lib/utils';

import { SectionCard, money, shortDate, StatGridSkeleton, CardSkeleton, ListSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';
import { SaveRow, Toggle } from './settings/controls';

type ShipmentStage = 'preparing' | 'in_transit' | 'delivered';

/** Order status → fulfilment stage, the same mapping the seller's Shipping tab uses. */
const STAGE_OF: Record<string, ShipmentStage> = {
  paid: 'preparing',
  processing: 'preparing',
  shipped: 'in_transit',
  delivered: 'delivered',
};

const STATUS: Record<ShipmentStage, { label: string; tone: string; icon: React.ReactNode }> = {
  preparing: { label: 'Preparing', tone: 'bg-amber-100 text-amber-800', icon: <Package className="h-3.5 w-3.5" /> },
  in_transit: { label: 'In transit', tone: 'bg-blue-100 text-blue-800', icon: <Truck className="h-3.5 w-3.5" /> },
  delivered: { label: 'Delivered', tone: 'bg-green-100 text-green-800', icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
};

const slug = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 32);

/**
 * The seller's shipping options (`store_settings.shipping_options`), edited
 * through the seller's settings rules. Checkout still prices delivery per
 * listing; these are what the storefront and Store Manager show.
 */
const ShippingOptionsCard: React.FC<{ store: AdminStore; options: StoreShippingOption[]; configured: boolean }> = ({
  store,
  options,
  configured,
}) => {
  const [rows, setRows] = useState<StoreShippingOption[]>(options);
  const [confirm, setConfirm] = useState<null | 'save' | 'reset'>(null);
  const update = useUpdateStoreSettings();
  const canEdit = useGate(PERMISSIONS.store.settings).allowed;
  const dirty = JSON.stringify(rows) !== JSON.stringify(options);
  const keys = rows.map((r) => r.key);
  const valid = rows.every((r) => r.key && r.label.trim() && r.price >= 0) && new Set(keys).size === keys.length;
  const edit = (i: number, patch: Partial<StoreShippingOption>) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <SectionCard
      title="Shipping options"
      description={configured ? 'Set by the seller.' : 'Defaults — the seller has not configured shipping yet.'}
      actions={
        configured ? (
          <PermissionButton code={PERMISSIONS.store.settings} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setConfirm('reset')}>
            Reset to defaults
          </PermissionButton>
        ) : undefined
      }
    >
      <div className="space-y-2">
        {rows.map((o, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Toggle disabled={!canEdit} checked={o.enabled} onChange={(v) => edit(i, { enabled: v })} />
            <Input disabled={!canEdit} value={o.label} onChange={(e) => edit(i, { label: e.target.value, key: o.key || slug(e.target.value) })} className="h-8 min-w-[140px] flex-1" />
            <Input disabled={!canEdit} type="number" min={0} step="0.01" value={o.price} onChange={(e) => edit(i, { price: Number(e.target.value) })} className="h-8 w-24" title="Price (GHS)" />
            <Input disabled={!canEdit} value={o.eta ?? ''} onChange={(e) => edit(i, { eta: e.target.value || null })} className="h-8 w-28" placeholder="ETA" />
            <label className="flex items-center gap-1.5 text-xs text-gray-600">
              <Toggle disabled={!canEdit} checked={Boolean(o.pay_on_fulfilment)} onChange={(v) => edit(i, { pay_on_fulfilment: v })} />
              {o.key === 'pickup' ? 'Pay on pickup' : 'Pay on delivery'}
            </label>
            {canEdit && (
              <button type="button" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600" aria-label="Remove option">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        {canEdit && rows.length < 10 && (
          <button
            type="button"
            onClick={() => setRows((rs) => [...rs, { key: `option-${rs.length + 1}`, label: 'New option', price: 0, eta: null, enabled: true }])}
            className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-900"
          >
            <Plus className="h-3.5 w-3.5" /> Add option
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-400">{rows.filter((r) => r.enabled).map((r) => `${r.label} ${r.price === 0 ? 'free' : money(r.price)}`).join(' · ') || 'No options enabled'}</p>
      <SaveRow dirty={dirty && valid} saving={update.isPending} onSave={() => setConfirm('save')} onReset={() => setRows(options)} />
      {confirm && (
        <ReasonDialog
          open
          title={confirm === 'save' ? 'Save shipping options' : 'Reset shipping options'}
          description={confirm === 'save' ? `${rows.length} option(s). The seller is notified.` : 'Clears the seller’s options; the defaults apply again.'}
          confirmLabel={confirm === 'save' ? 'Save' : 'Reset'}
          destructive={confirm === 'reset'}
          reasonRequired
          loading={update.isPending}
          onConfirm={(reason) =>
            update.mutate(
              { storeid: store.storeid, reason, shipping_options: confirm === 'save' ? rows : [] },
              { onSuccess: () => setConfirm(null) },
            )
          }
          onClose={() => setConfirm(null)}
        />
      )}
    </SectionCard>
  );
};

const stageOf = (o: StoreOrder): ShipmentStage | undefined => STAGE_OF[String(o.status)];

const pickupSummary = (pickup: unknown): string => {
  if (!pickup) return '—';
  if (typeof pickup === 'string') return pickup;
  const p = pickup as Record<string, unknown>;
  return (p.address as string) || (p.gpgpsCode as string) || (p.reference as string) || '—';
};

export const FulfilmentTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const { data: settings, isLoading } = useStoreSettingsQuery(store.storeid);
  // Shipments are orders past payment. The admin list filters one status at a
  // time, so read the recent window and bucket client-side (as ff-ui does).
  const { data: orderData, isLoading: loadingOrders } = useStoreOrderListQuery(store.storeid, { limit: 100 });
  const shipments = (orderData?.orders ?? []).filter((o) => stageOf(o));

  if (isLoading || loadingOrders)
    return (
      <div className="space-y-4">
        <StatGridSkeleton count={3} />
        <CardSkeleton description><ListSkeleton rows={5} /></CardSkeleton>
      </div>
    );

  const counts = {
    in_transit: shipments.filter((o) => stageOf(o) === 'in_transit').length,
    delivered: shipments.filter((o) => stageOf(o) === 'delivered').length,
    preparing: shipments.filter((o) => stageOf(o) === 'preparing').length,
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {([['In transit', counts.in_transit], ['Delivered', counts.delivered], ['Preparing', counts.preparing]] as const).map(
          ([label, n]) => (
            <div key={label} className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
              <p className="mt-1 text-xl font-bold text-gray-900">{n}</p>
            </div>
          ),
        )}
      </div>

      <SectionCard title="Recent shipments" description="Paid orders and their delivery stage (latest 100 orders).">
        {shipments.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">No shipments yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {shipments.map((o) => {
              const st = STATUS[stageOf(o)!];
              return (
                <div key={o.orderid} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      Order #{o.orderid} · {money(o.amount_total)} · {o.item_count ?? '—'} item{o.item_count === 1 ? '' : 's'}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{o.buyer_username ? `@${o.buyer_username}` : `User #${o.uid_buyer ?? '—'}`}</span>
                      <span>· {o.tracking_number ? `Tracking ${o.tracking_number}` : 'No tracking yet'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="hidden text-xs text-gray-400 sm:inline">{shortDate(o.date_paid ?? o.date_added)}</span>
                    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold', st.tone)}>
                      {st.icon}
                      {st.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      <div className="grid gap-4 md:grid-cols-2">
        <ShippingOptionsCard
          key={JSON.stringify(settings?.shipping_options ?? [])}
          store={store}
          options={settings?.shipping_options ?? []}
          configured={Boolean(settings?.shipping_options_configured)}
        />

        <SectionCard title="Default pickup location">
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <span>{pickupSummary(settings?.pickup_address)}</span>
          </div>
          <p className="mt-2 text-xs text-gray-400">From the store settings. Edit under Settings → Visit.</p>
        </SectionCard>
      </div>
    </div>
  );
};
